import { useQueryClient } from '@tanstack/react-query';
import { useCallback, useEffect, useRef, useState } from 'react';

import type { IWifiConfigUpdateStatusResponse, IWifiNetworkRow } from '@/service/vpc/types/IWifiConfiguration';

import { getWifiConfigurationQueryOptions } from '@/service/vpc/queryOptions';
import { vpcService } from '@/service/vpc/vpcService';

type PollingStatus = 'idle' | 'polling' | 'success' | 'failure';

const POLL_INTERVAL_MS = 3000;
const MAX_CONSECUTIVE_ERRORS = 5; // used for initial load only

const parseNetworksFromResponse = (response: IWifiConfigUpdateStatusResponse): IWifiNetworkRow[] => {
  // Primary: wifiStatusHistory contains the fullest network details
  const historyEntry = response.wifiStatusHistory?.wifiStatusHistoryResponse?.wifiStatuses?.find(
    (s) => s.wifiStatus.configuredWifiNetworks.length > 0
  );

  if (historyEntry) {
    const { wifiStatus, receivedDateTime } = historyEntry;
    const connectedSsid = wifiStatus.connectedWifiNetwork?.ssid;
    return wifiStatus.configuredWifiNetworks
      .map((network) => ({
        ssid: network.ssid,
        wifiFrequency: network.wifiFrequency,
        priority: network.priority,
        signalStrength: network.signalStrength,
        connectionDateTime: network.connectionDateTime,
        isConnected: network.ssid === connectedSsid,
        receivedDateTime,
      }))
      .sort((a, b) => a.priority - b.priority);
  }

  // Fallback: read directly from wifiStatusResponse.wifiStatus
  const wifiStatus = response.wifiStatusResponse?.wifiStatusResponse?.wifiStatus;
  if (!wifiStatus) return [];

  const connectedSsid = wifiStatus.connectedWifiNetwork?.ssid;
  return wifiStatus.configuredWifiNetworks
    .map((network) => ({
      ssid: network.ssid,
      wifiFrequency: network.wifiFrequency,
      priority: network.priority,
      signalStrength: network.signalStrength,
      connectionDateTime: network.connectionDateTime,
      isConnected: network.ssid === connectedSsid,
      receivedDateTime: new Date().toISOString(),
    }))
    .sort((a, b) => a.priority - b.priority);
};

export const useWifiConfigPolling = ({ chassisId }: { chassisId?: string }) => {
  const queryClient = useQueryClient();
  const [status, setStatus] = useState<PollingStatus>('idle');
  const abortRef = useRef<AbortController | null>(null);
  const intervalRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  const cleanup = useCallback(() => {
    abortRef.current?.abort();
    abortRef.current = null;
    if (intervalRef.current) clearTimeout(intervalRef.current);
    intervalRef.current = null;
  }, []);

  /**
   * Generic async poller. Awaits each tick sequentially then waits POLL_INTERVAL_MS
   * before the next. Stops when tick returns true (done) or the signal is aborted.
   */
  const runPolling = useCallback(
    async (tick: (signal: AbortSignal) => Promise<boolean>, signal: AbortSignal, maxErrors?: number) => {
      let consecutiveErrors = 0;

      while (!signal.aborted) {
        await new Promise((resolve) => {
          intervalRef.current = setTimeout(resolve, POLL_INTERVAL_MS);
        });

        if (signal.aborted) break;

        try {
          const done = await tick(signal);
          if (signal.aborted) break;

          consecutiveErrors = 0;
          if (done) break;
        } catch (err) {
          if (signal.aborted) break;
          consecutiveErrors++;
          console.warn(`[useWifiConfigPolling] Poll error (${consecutiveErrors}/${maxErrors ?? '∞'})`, err);
          if (maxErrors != null && consecutiveErrors >= maxErrors) {
            cleanup();
            setStatus('failure');
            break;
          }
        }
      }
    },
    [cleanup]
  );

  // Initial load:
  // 1. GET wificonfig/{chassisId} → correlationId
  // 2. Poll GET wificonfig/{chassisId}/{correlationId} until success → parse & display networks
  const fetchConfigs = useCallback(
    async (abortController: AbortController) => {
      try {
        const response = await vpcService.Services.getWifiConfig({
          chassisId: chassisId!,
          signal: abortController.signal,
        });
        const correlationId = response.correlationId;

        if (!correlationId) {
          cleanup();
          setStatus('success');
          return;
        }

        void runPolling(
          async (signal) => {
            const res = await vpcService.Services.getWifiConfigByCorrelationId({
              chassisId: chassisId!,
              correlationId,
              signal,
            });

            const requestStatus =
              res.wifiStatusResponse?.wifiStatusResponse?.requestStatus?.status?.toLowerCase() ??
              res.status?.toLowerCase();

            if (requestStatus === 'success') {
              const networks = parseNetworksFromResponse(res);
              queryClient.setQueryData(getWifiConfigurationQueryOptions({ chassisId: chassisId! }).queryKey, networks);
              cleanup();
              setStatus('success');
              return true;
            }
            if (requestStatus === 'failure') {
              cleanup();
              setStatus('failure');
              return true;
            }
            return false; // pending / null → keep polling
          },
          abortController.signal,
          MAX_CONSECUTIVE_ERRORS
        );
      } catch {
        if (!abortController.signal.aborted) {
          cleanup();
          setStatus('failure');
        }
      }
    },
    [chassisId, cleanup, queryClient, runPolling]
  );

  const startPolling = useCallback(
    async (updateCorrelationId?: string, updatedNetworks?: IWifiNetworkRow[]) => {
      if (!chassisId) return;

      cleanup();
      setStatus('polling');

      const abortController = new AbortController();
      abortRef.current = abortController;

      if (!updateCorrelationId) {
        // Initial load — fetch configs
        await fetchConfigs(abortController);
        return;
      }

      // Update: poll wificonfigupdatestatus until the device acknowledges, then update table directly
      void runPolling(async (signal) => {
        const response = await vpcService.Services.getWifiConfigUpdateStatus({
          chassisId,
          correlationId: updateCorrelationId,
          signal,
        });

        const s = response.status?.toLowerCase();

        if (s === 'success') {
          if (updatedNetworks) {
            queryClient.setQueryData(getWifiConfigurationQueryOptions({ chassisId }).queryKey, updatedNetworks);
          } else {
            await fetchConfigs(abortController);
          }
          cleanup();
          setStatus('success');
          return true;
        }
        if (s === 'failure') {
          cleanup();
          setStatus('failure');
          return true;
        }
        return false; // pending → keep polling
      }, abortController.signal);
    },
    [chassisId, cleanup, fetchConfigs, queryClient, runPolling]
  );

  const reset = useCallback(() => {
    cleanup();
    setStatus('idle');
  }, [cleanup]);

  useEffect(() => {
    return cleanup;
  }, [cleanup]);

  return {
    status,
    isPolling: status === 'polling',
    startPolling,
    reset,
  };
};
